import { useEffect, useState } from 'react';
import axios from 'axios';

import { ErrorPage } from '../../../ErrorPage';
import { LoadingPage } from '../../../LoadingPage';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { Button } from '../components/Button';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField } from '../components/CheckboxField';
import { DisclaimerBox } from '../components/DisclaimerBox';
import { DataCard } from '../components/DataCard';
import * as S from './styles';

interface ResumeOperationProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
}

const formatDate = (inputDate: string): string => {
    const date = new Date(inputDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value / 100);
};

export const CreditOperationAndSocialSecurityCardsResumeOperation = ({
    signData,
    setSignerStep,
    setSignData,
}: ResumeOperationProps) => {
    const { clientThemeSettings } = useThemeContext();
    const initialError = { has_error: false, status: 0 };

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(true);
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [isChecked, setIsChecked] = useState(false);

    useEffect(() => {
        setLoading(false);
    }, []);

    const handleDownload = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        try {
            const response = await fetch(documentUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'termo-contratacao-cartao-beneficio.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            window.open(documentUrl, '_blank');
        }
    };

    const handleClick = async () => {
        if (hasMadeRequest) return;

        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        const payload = {
            step: 'resume_operation',
            event_date: new Date().toISOString(),
            device_data: { device_key: browserKey },
        };

        const config = {
            headers: { Authorization: signData.signer_token },
        };

        setLoading(true);
        setHasMadeRequest(true);

        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, ...response.data });
            })
            .catch((error) => {
                const status = [401, 403, 409].includes((error.response || {}).status)
                    ? error.response.status
                    : 500;
                setError({ has_error: true, status });
                setHasMadeRequest(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    if (loading) {
        return <LoadingPage clientThemeSettings={clientThemeSettings} />;
    }

    const op = signData.credit_operation_data;
    const documentUrl = signData?.document_url;
    const operationType = op?.operation_type || 'structured_operation';
    const accentColor = clientThemeSettings?.font_color;

    return (
        <S.PageWrapper>
            <S.ScrollContent>
                <S.Header>
                    {clientThemeSettings?.logo_url && (
                        <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                    )}
                </S.Header>

                <S.HeroSection>
                    <S.Title $color={accentColor}>
                        <strong>Resumo</strong> do seu{'\n'}empréstimo
                    </S.Title>
                </S.HeroSection>

                <S.DataCardContainer>
                    <DataCard.Root>
                        {operationType === 'structured_operation' ? (
                            <>
                                <DataCard.Row
                                    label="Valor total do empréstimo"
                                    value={formatCurrency(op.issue_amount)}
                                    color={accentColor}
                                />
                                <DataCard.Row
                                    label="IOF"
                                    value={formatCurrency(op.iof)}
                                    color={accentColor}
                                />
                                <DataCard.Row
                                    label="Valor líquido a receber"
                                    value={<>{formatCurrency(op.disbursed_amount)}*</>}
                                    color={accentColor}
                                />
                            </>
                        ) : (
                            <>
                                <DataCard.Row
                                    label={
                                        operationType === 'refinancing'
                                            ? 'Valor de pagamento de dívida'
                                            : 'Valor liberado - quitação de dívida'
                                    }
                                    value={formatCurrency(op.refinancing?.due_balance)}
                                    color={accentColor}
                                />
                                <DataCard.Row
                                    label={
                                        operationType === 'refinancing'
                                            ? 'Valor liberado ao emitente'
                                            : 'Valor liberado - livre utilização'
                                    }
                                    value={formatCurrency(op.refinancing?.final_disbursed_amount)}
                                    color={accentColor}
                                />
                            </>
                        )}
                        <DataCard.Row label="CET (a.m.)" value={`${op.monthly_cet}% ao mês`} color={accentColor} />
                        <DataCard.Row label="Taxa de juros mensal" value={`${op.monthly_interest_rate}%`} color={accentColor} />
                        <DataCard.Row label="Número de parcelas" value={op.number_of_installments} color={accentColor} />
                        <DataCard.Row label="Data da primeira parcela" value={formatDate(op.first_due_date)} color={accentColor} />
                        {op.interest_amount != null && (
                            <DataCard.Row label="Juros a pagar" value={formatCurrency(op.interest_amount)} color={accentColor} />
                        )}
                        {op.total_amount != null && (
                            <DataCard.Row label="Total a pagar" value={formatCurrency(op.total_amount)} color={accentColor} />
                        )}
                    </DataCard.Root>
                    {operationType === 'structured_operation' && (
                        <S.DisclaimerSection>
                            <S.DisclaimerText>
                                * Esse valor pode sofrer uma pequena variação no momento de desembolso.
                            </S.DisclaimerText>
                            {documentUrl && (
                                <DisclaimerBox mode="info">
                                    Para mais informações,{' '}
                                    <a href={documentUrl} onClick={handleDownload}>
                                        consulte o contrato de empréstimo.
                                    </a>
                                </DisclaimerBox>
                            )}
                        </S.DisclaimerSection>
                    )}
                </S.DataCardContainer>

                <S.CheckboxWrapper>
                    <CheckboxField
                        id="resume-operation-terms"
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                        accentColor={accentColor}
                    >
                        <strong>Declaro que li e estou de acordo</strong> com as condições do contrato acima.
                    </CheckboxField>
                </S.CheckboxWrapper>
            </S.ScrollContent>

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}

            <S.FooterSection>
                <Button
                    clientThemeSettings={clientThemeSettings}
                    disabled={!isChecked}
                    onClick={handleClick}
                >
                    Aceitar termos de contrato
                </Button>

                <PoweredByQITech />
            </S.FooterSection>
        </S.PageWrapper>
    );
};
